import { useState, useCallback, useRef } from "react";
import { Plug, Database, Loader2, X } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Hint } from "@/components/ui/hint";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { Connection, ConnectionStatus } from "@optidev/shared-types";
import { useAuth } from "@/contexts/AuthContext";
import { getActiveWorkspaceId } from "@/stores/workspace-store";
import { cn } from "@/lib/utils";

interface ProjectConnectionsPopoverProps {
  projectId: string;
  connectionIds: string[];
  onRemoveConnection?: (connection: Connection) => void;
  isAgentStreaming?: boolean;
}

const getConnectorIcon = (connectorId: string | undefined) => {
  switch (connectorId) {
    case "mongodb":
      return <Database className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />;
    case "bigquery":
    case "google-bigquery":
      return <Database className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />;
    case "postgresql":
      return <Database className="h-3.5 w-3.5 text-indigo-500 flex-shrink-0" />;
    case "mysql":
      return <Database className="h-3.5 w-3.5 text-orange-500 flex-shrink-0" />;
    default:
      return (
        <Database className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
      );
  }
};

const getStatusDot = (status: ConnectionStatus) => {
  switch (status) {
    case ConnectionStatus.ACTIVE:
      return <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />;
    case ConnectionStatus.ERROR:
      return <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />;
    case ConnectionStatus.PENDING:
      return <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />;
    default:
      return <div className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />;
  }
};

const formatTypeName = (type: string | undefined): string => {
  if (!type) return "Unknown";
  return type
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function ProjectConnectionsPopover({
  projectId,
  connectionIds,
  onRemoveConnection,
  isAgentStreaming = false,
}: ProjectConnectionsPopoverProps) {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const { getIdToken } = useAuth();
  const lastFetchedIdsRef = useRef<string>("");

  const fetchConnections = useCallback(async () => {
    if (!connectionIds.length) {
      setConnections([]);
      return;
    }

    const idsKey = connectionIds.join(",");
    if (idsKey === lastFetchedIdsRef.current && connections.length > 0) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = await getIdToken();
      if (!token) throw new Error("Authentication required");

      const workspaceId = getActiveWorkspaceId();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/editor/projects/${projectId}/connections`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(workspaceId && { "X-Workspace-Id": workspaceId }),
          },
        },
      );

      if (!response.ok) throw new Error("Failed to load connections");

      const data = await response.json();
      setConnections(data);
      lastFetchedIdsRef.current = idsKey;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [projectId, connectionIds, getIdToken, connections.length]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      fetchConnections();
    }
  };

  const count = connectionIds.length;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Hint text="Connections" side="bottom" align="center" delayDuration={100}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              "relative h-8 w-8 inline-flex items-center justify-center rounded-md flex-shrink-0 transition-colors",
              "border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/80",
            )}
          >
            <Plug className="w-4 h-4" />
            {count > 0 && (
              <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-blue-500 text-[10px] font-semibold text-white ring-2 ring-background">
                {count}
              </span>
            )}
          </button>
        </PopoverTrigger>
      </Hint>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-72 p-0"
      >
        <div className="px-3 py-2.5 border-b border-border">
          <h4 className="text-sm font-medium">Project Connections</h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            External services linked to this project
          </p>
        </div>

        <div className="max-h-[280px] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && !loading && (
            <div className="px-3 py-4 text-center">
              <p className="text-xs text-red-500">{error}</p>
            </div>
          )}

          {!loading && !error && connections.length === 0 && (
            <div className="px-3 py-6 text-center">
              <Plug className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">
                No connections linked yet
              </p>
            </div>
          )}

          {!loading && !error && connections.length > 0 && (
            <div className="py-1">
              {connections.map((connection) => (
                <div
                  key={connection.id}
                  className="group flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 transition-colors"
                >
                  {getConnectorIcon(connection.type)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[12px] font-medium truncate">
                        {connection.name}
                      </span>
                      {getStatusDot(connection.status)}
                    </div>
                    <span className="text-[10px] text-muted-foreground leading-none">
                      {formatTypeName(connection.type)}
                    </span>
                  </div>
                  {onRemoveConnection && (
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            disabled={isAgentStreaming}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpen(false);
                              onRemoveConnection(connection);
                            }}
                            className={cn(
                              "flex-shrink-0 h-6 w-6 inline-flex items-center justify-center rounded-md transition-colors",
                              isAgentStreaming
                                ? "text-muted-foreground/40 cursor-not-allowed"
                                : "text-muted-foreground hover:text-destructive hover:bg-destructive/10",
                            )}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        {isAgentStreaming && (
                          <TooltipContent side="left" className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 px-2 py-1 shadow-md">
                            <p className="text-xs font-medium">Wait for the agent to be ready</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
